import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { formatNumber, timeAgo } from "@/lib/format";
import { FilmSlateIcon, ImageIcon, CircleNotchIcon, ChartBarIcon } from "@phosphor-icons/react";

const Stat = ({ label, value, sub, icon: Icon, accent }) => (
  <div className="border border-[#222226] bg-[#0a0a0c] p-4" data-testid={`usage-stat-${label}`}>
    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-[#5a5a63]">
      <Icon size={14} style={{ color: accent }} /> {label}
    </div>
    <div className="font-rajdhani font-bold text-3xl text-white mt-1">{value}</div>
    {sub && <div className="font-mono text-[11px] text-[#5a5a63] mt-0.5">{sub}</div>}
  </div>
);

export const UsageDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/usage/summary");
        setData(res.data);
      } catch { /* noop */ } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex items-center justify-center py-32" data-testid="usage-loading"><CircleNotchIcon size={36} className="text-[#16C784] spin-slow" /></div>;
  if (!data) return <div className="text-center py-20 font-mono text-sm text-[#5a5a63]">No usage data yet.</div>;

  const maxDay = Math.max(1, ...data.by_day.map((d) => d.videos + d.images));

  return (
    <div data-testid="usage-dashboard" className="space-y-5 max-w-[1100px]">
      <div>
        <h2 className="font-rajdhani font-bold text-2xl tracking-wide text-white flex items-center gap-2">
          <ChartBarIcon size={22} weight="fill" className="text-[#16C784]" /> Usage Dashboard
        </h2>
        <p className="font-mono text-xs text-[#5a5a63] mt-1">AI video &amp; image generation requests across your account</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Videos" value={formatNumber(data.video_count)} sub="AtlasCloud · Seedance 1.5 Pro" icon={FilmSlateIcon} accent="#8B5CF6" />
        <Stat label="Video Seconds" value={formatNumber(data.video_seconds)} sub={`~${Math.round(data.video_seconds / 60)} min generated`} icon={FilmSlateIcon} accent="#A78BFA" />
        <Stat label="Images" value={formatNumber(data.image_count)} sub="OpenAI · gpt-image-1" icon={ImageIcon} accent="#16C784" />
        <Stat label="720p Clips" value={formatNumber(data.by_resolution?.["720p"] || 0)} sub={`${data.by_resolution?.["480p"] || 0} at 480p`} icon={FilmSlateIcon} accent="#A3E635" />
      </div>

      {/* Per-day bars */}
      {data.by_day.length > 0 && (
        <div className="border border-[#222226] bg-[#0a0a0c] p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#5a5a63] mb-3">Requests · last 14 days</div>
          <div className="flex items-end gap-2 h-40" data-testid="usage-daybars">
            {[...data.by_day].reverse().map((d) => (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group">
                <div className="w-full flex flex-col justify-end items-center" style={{ height: "130px" }}>
                  <div className="w-full bg-[#8B5CF6]" style={{ height: `${(d.videos / maxDay) * 100}%` }} title={`${d.videos} videos`} />
                  <div className="w-full bg-[#16C784]" style={{ height: `${(d.images / maxDay) * 100}%` }} title={`${d.images} images`} />
                </div>
                <div className="font-mono text-[8px] text-[#5a5a63]">{d.date.slice(5)}</div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 mt-3 font-mono text-[10px] text-[#5a5a63]">
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#8B5CF6] inline-block" /> Videos</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#16C784] inline-block" /> Images</span>
          </div>
        </div>
      )}

      {/* Recent requests */}
      <div className="border border-[#222226] bg-[#0a0a0c] p-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#5a5a63] mb-2">Recent requests</div>
        <div className="space-y-1" data-testid="usage-recent">
          {data.recent.map((l, i) => (
            <div key={i} className="flex items-center gap-3 font-mono text-[11px] py-1 border-b border-[#161619] last:border-0">
              <span className={l.kind === "video" ? "text-[#A78BFA]" : "text-[#16C784]"}>
                {l.kind === "video" ? <FilmSlateIcon size={13} /> : <ImageIcon size={13} />}
              </span>
              <span className="text-white w-40 truncate">{l.service}</span>
              <span className="text-[#A0A0AB] w-20">{l.resolution || l.size || "—"}</span>
              <span className="text-[#5a5a63] w-16">{l.duration_seconds ? `${l.duration_seconds}s` : ""}</span>
              <span className="text-[#5a5a63] ml-auto">{timeAgo(l.created_at)}</span>
            </div>
          ))}
          {data.recent.length === 0 && <div className="font-mono text-xs text-[#5a5a63] py-3">No requests logged yet.</div>}
        </div>
      </div>
    </div>
  );
};

export default UsageDashboard;
